import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

const STAGE_COLORS = {
  "Live": "bg-emerald-500",
  "Awaiting Signature": "bg-amber-500",
  "Approved": "bg-blue-500",
  "GC Review": "bg-violet-500",
  "With Vendor": "bg-cyan-500",
  "In Review": "bg-slate-500",
  "Received": "bg-slate-400",
};

export default function FlightDisplay() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await axios.get(`${API}/public/flight/${token}`);
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.status === 404 ? "Display revoked or invalid token." : "Unable to load.");
      }
    };
    load();
    const int = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(int); };
  }, [token]);

  useEffect(() => {
    if (!data?.items?.length) return;
    const t = setInterval(() => setIdx(i => (i + 1) % data.items.length), 6000);
    return () => clearInterval(t);
  }, [data]);

  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-black text-white text-2xl" data-testid="flight-error">
      {error}
    </div>
  );
  if (!data) return (
    <div className="min-h-screen flex items-center justify-center bg-black text-white text-xl">Loading…</div>
  );

  const cur = data.items[idx];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0F172A] to-[#001a2c] text-white flex flex-col" data-testid="flight-display">
      <div className="p-8 border-b border-white/10 flex items-baseline justify-between">
        <div>
          <div className="text-4xl font-black tracking-tight">HCD Contract Flight Status</div>
          <div className="text-white/60 text-sm mt-1">{data.display_name}{data.location ? ` · ${data.location}` : ""}</div>
        </div>
        <div className="text-white/50 text-sm">{new Date(data.generated_at).toLocaleTimeString()}</div>
      </div>

      <div className="flex-1 grid grid-cols-5 gap-8 p-8">
        <div className="col-span-3 flex flex-col justify-center">
          {cur ? (
            <div data-testid="flight-current">
              <div className="text-white/40 text-lg mb-3">Now Live</div>
              <div className="text-7xl font-black leading-tight break-words">{cur.vendor}</div>
              <div className="text-3xl text-white/70 mt-4">#{cur.contract_number}</div>
              <div className="mt-8 flex flex-wrap gap-3">
                <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 text-lg">
                  <span className={`w-3 h-3 rounded-full ${STAGE_COLORS[cur.stage_label] || "bg-white/40"}`} />
                  {cur.stage_label}
                </span>
                <span className="inline-flex px-4 py-2 rounded-full bg-white/10 text-lg">{cur.department}</span>
                {cur.auto_renewal && <span className="inline-flex px-4 py-2 rounded-full bg-amber-500/20 text-amber-200 text-lg">Auto-Renew</span>}
              </div>
            </div>
          ) : (
            <div className="text-white/50 text-xl">No contracts to display.</div>
          )}
        </div>
        <div className="col-span-2 space-y-2 overflow-hidden">
          <div className="text-white/40 text-sm uppercase tracking-widest mb-3">Recent activity ({data.items.length})</div>
          {data.items.slice(0, 12).map((it, i) => (
            <div
              key={it.id}
              className={`px-4 py-3 rounded-lg flex items-center gap-3 transition ${i === idx ? "bg-white/20" : "bg-white/5"}`}
              data-testid={`flight-item-${it.contract_number}`}
            >
              <span className={`w-2.5 h-2.5 rounded-full ${STAGE_COLORS[it.stage_label] || "bg-white/40"}`} />
              <div className="flex-1 min-w-0">
                <div className="text-sm truncate">{it.vendor}</div>
                <div className="text-xs text-white/40">#{it.contract_number} · {it.department}</div>
              </div>
              <div className="text-xs text-white/60 whitespace-nowrap">{it.stage_label}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
